<template>
  <div class="folder-box">
    <div class="folder-header">
      <div class="header-left">
        <span class="title">文件夹</span>
        <!-- <a-tooltip title="提示">
          <InfoCircleOutlined class="icon-info" />
        </a-tooltip> -->
      </div>
      <div class="header-right" v-if="!readonly">
        <a-tooltip title="新建文件夹">
          <FolderAddOutlined class="icon-add" @click="handleAddFolder" />
        </a-tooltip>
      </div>
    </div>
    <div class="folder-list">
      <a-spin :spinning="loading">
        <div v-if="folderList.length">
          <FolderItem
            v-for="item in folderList"
            :key="item.id"
            :item="item"
            :active-id="activeFolderId"
            :editing-id="editingId"
            :readonly="readonly"
            @select="handleSelectFolder"
            @delete="handleDeleteFolder"
            @rename="handleRename"
            @edit="handleEditStart"
            @move="handleMove"
          />
        </div>
      </a-spin>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, reactive, createVNode } from "vue";
import {
  FolderAddOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons-vue";
import {
  getWaylineFolders,
  saveWaylineFolderApi,
  updateWaylineFolderApi,
  deleteWaylineFolderApi,
} from "/@/api/wayline";
import { ELocalStorageKey } from "/@/types";
import { message, Modal } from "ant-design-vue";
import FolderItem from "./FolderItem.vue";

const props = defineProps({
  readonly: {
    type: Boolean,
    default: false,
  },
  currentFolderId: {
    type: [String, Number],
    default: "",
  },
});

const workspaceId = localStorage.getItem(ELocalStorageKey.WorkspaceId)!;
const loading = ref(false);
const folderList = ref<any[]>([]);
const activeFolderId = ref<string | number>("");
const editingId = ref<string | number>("");

const getFolders = async () => {
  // If we are just refreshing list after add/edit, we might not want to show full loading spinner if it causes flicker
  // But for now let's keep it simple
  // loading.value = true;
  try {
    const res = await getWaylineFolders(workspaceId);
    if (res.code === 0) {
      if (res.data) {
        folderList.value = [res.data];
        if (props.currentFolderId) {
          activeFolderId.value = props.currentFolderId;
        }
        // Default select the root if nothing selected
        if (res.data.id && !activeFolderId.value) {
          activeFolderId.value = res.data.id;
          emit("select", res.data);
        }
      } else {
        folderList.value = [];
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    // loading.value = false;
  }
};

// Initial load
const initFolders = async () => {
  loading.value = true;
  await getFolders();
  loading.value = false;
};

const emit = defineEmits(["select"]);

const handleSelectFolder = (item: any) => {
  // If currently editing, don't change selection or maybe confirming edit is handled by blur
  activeFolderId.value = item.id;
  emit("select", item);
};

const handleAddFolder = async () => {
  if (!activeFolderId.value) {
    message.warning("请先选择一个父文件夹");
    return;
  }

  loading.value = true;
  try {
    // Calculate a default name, maybe "新建文件夹" or based on existing children count (hard to do without traversing tree locally)
    // For now use a static default name, or "文件夹(1)" as user requested example
    const defaultName = "新建文件夹";

    const params = {
      name: defaultName,
      workspace_id: workspaceId,
      folder_id: activeFolderId.value,
    };
    const res = await saveWaylineFolderApi(params);
    if (res.code === 0) {
      // Refresh list to get the new item with its ID
      await getFolders();

      // We need to find the newly created folder ID to enter edit mode
      // Since the API returns the ID usually, let's use that.
      // Assuming res.data is the created object or ID.
      // If res.data is just boolean/null, we have a problem.
      // Usually save returns the new object.
      const newId = res.data?.id || res.data;

      if (newId) {
        editingId.value = newId;
        // Also select it? User preference. Usually creating selects it.
        // activeFolderId.value = newId;
      }
      message.success("新建成功");
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const handleEditStart = (id: any) => {
  editingId.value = id;
};

const handleRename = async (payload: { id: number; name: string }) => {
  // Stop editing mode first
  const currentId = editingId.value;
  // editingId.value = ""; // Don't clear immediately if we want to show error on failure?

  if (!payload.name) {
    editingId.value = "";
    return;
  }

  // Check if name actually changed? The child component does this check mostly, but good to be safe
  // But we don't have the old name easily here without traversing.

  try {
    const updateParams = {
      id: payload.id,
      name: payload.name,
      workspace_id: workspaceId,
    };

    await updateWaylineFolderApi(updateParams);
    // message.success("修改成功"); // Optional, maybe too noisy for simple rename
    editingId.value = "";
    await getFolders();
  } catch (error) {
    console.error(error);
    editingId.value = ""; // Stop editing on error?
  }
};

const handleMove = async (payload: { id: number; targetId: number }) => {
  if (payload.id === payload.targetId) return;

  try {
    // We need the current name of the folder being moved, but if the API allows partial update or if we don't have easy access,
    // we might assume backend handles it or we need to traverse to find name.
    // Assuming update API can handle just ID and folder_id move, OR we need to send name.
    // Based on previous update usage, we sent name.
    // Let's find the item recursively to get its name.
    const findItem = (list: any[], id: number): any => {
      for (const item of list) {
        if (item.id === id) return item;
        if (item.children) {
          const found = findItem(item.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const item = findItem(folderList.value, payload.id);
    if (!item) return;

    const params = {
      id: payload.id,
      name: item.name,
      folder_id: payload.targetId,
      workspace_id: workspaceId,
    };

    await updateWaylineFolderApi(params);
    message.success("移动成功");
    await getFolders();
  } catch (error) {
    console.error(error);
  }
};

const handleDeleteFolder = (id: number) => {
  const findItemName = (list: any[], targetId: number): string => {
    for (const item of list) {
      if (item.id === targetId) return item.name;
      if (item.children) {
        const name = findItemName(item.children, targetId);
        if (name) return name;
      }
    }
    return "";
  };

  const folderName = findItemName(folderList.value, id);

  Modal.confirm({
    title: "提示",
    icon: createVNode(ExclamationCircleOutlined),
    content: `是否删除“${folderName}”文件夹，删除后，该文件夹及子文件夹内的所有航线也将一并被删除，请确认是否删除`,
    centered: true,
    class: "modal-class",
    async onOk() {
      try {
        loading.value = true;
        const res = await deleteWaylineFolderApi(id);
        if (res.code === 0) {
          message.success("删除成功");
          if (activeFolderId.value == id) {
            activeFolderId.value = "";
          }
          await getFolders();
        }
      } catch (error) {
        console.error(error);
      } finally {
        loading.value = false;
      }
    },
  });
};

onMounted(() => {
  initFolders();
});
</script>

<style scoped lang="scss">
.folder-box {
  width: 100%;
  height: 100%;
  background-color: #fff; /* Light background */
  color: #333; /* Dark text */
  display: flex;
  flex-direction: column;
  border-right: 1px solid #f0f0f0; /* Optional: separation line */
}

.folder-header {
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f0f0; /* Separator */

  .header-left {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: bold;
    color: #333;

    .title {
      margin-right: 8px;
    }

    .icon-info {
      font-size: 14px;
      color: #999;
      cursor: pointer;
    }
  }

  .header-right {
    .icon-add {
      font-size: 18px;
      cursor: pointer;
      color: #666;
      &:hover {
        color: #1890ff;
      }
    }
  }
}

.folder-list {
  flex: 1;
  overflow-y: auto;
  padding: 5px 0;
}

/* 滚动条样式 */
.folder-list::-webkit-scrollbar {
  width: 6px;
}
.folder-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
.folder-list::-webkit-scrollbar-track {
  background: transparent;
}
</style>